import type { ConsignmentLineWriteRequest, ConsignmentLineDetailResponse } from '@/entities/consignments';
import { matchColdChainProfile } from '@/shared/lib/cold-chain';
import type { ConsignmentLineDetailsFormValues } from '../model/types';

/**
 * Get default form values for consignment line details form
 * @returns Default form values
 */
export function getDefaultFormValues(): ConsignmentLineDetailsFormValues {
  return {
    productCategory: '',
    inventoryQuantity: 1,
    productLtuId: '28',
    description: '',
    handlingUnits: 1,
    handlingUnitTypeId: '11',
    totalWeight: 1,
    totalVolume: 1,
    totalValue: 1,
    ownerReference: '',
    expiryDate: '',
    batchNumber: '',
    productCode: '',
    subProjectCode: '',
    optionalField1: null,
    optionalField2: null,
    optionalField3: null,
    optionalField4: null,
    optionalField5: null,
    optionalField6: null,
    fragile: false,
    dangerous: false,
    regulated: false,
    coldChain: false,
    coldChainProfile: '',
    coldMinTemp: null,
    coldMaxTemp: null,
    dangerousGoodsUnNumber: '',
    handlingInstructions: '',
    showOnPackingList: false
  };
}

/**
 * Transforms form values to API request format for creating/updating consignment line
 * @param formValues The values from the consignment line details form
 * @param consignmentId The ID of the consignment (required for create, optional for update)
 * @returns The API request object formatted for the consignment line endpoint
 * Note: This function maps form fields to the expected API request structure, including handling optional fields and type conversions as needed.
 */
export function transformFormToRequest(
  formValues: ConsignmentLineDetailsFormValues,
  consignmentId?: string
): ConsignmentLineWriteRequest {
  return {
    consignmentId,
    productId: formValues.productCategory ? parseInt(formValues.productCategory) : undefined,
    inventoryQuantity: formValues.inventoryQuantity,
    productLtuId: Number(formValues.productLtuId),
    ownerItemDescription: formValues.description, // Text description
    ownerReference: formValues.ownerReference || undefined,
    subProjectCode: formValues.subProjectCode || undefined,
    handlingUnits: formValues.handlingUnits,
    handlingUnitTypeId: Number(formValues.handlingUnitTypeId),
    totalWeightKg: formValues.totalWeight,
    totalVolumeM3: formValues.totalVolume,
    totalValueUsd: formValues.totalValue || undefined,
    expiryDate: formValues.expiryDate || null,
    batchNumber: formValues.batchNumber || null,
    productCode: formValues.productCode || null,
    isFragile: formValues.fragile,
    isDangerous: formValues.dangerous,
    isRegulated: formValues.regulated,
    isColdChain: formValues.coldChain,
    coldMinTemp: formValues.coldMinTemp,
    coldMaxTemp: formValues.coldMaxTemp,
    dangerousGoodsUnId: formValues.dangerousGoodsUnNumber ? Number(formValues.dangerousGoodsUnNumber) : null,
    handlingInstructions: formValues.handlingInstructions || null,
    showOnPackingList: formValues.showOnPackingList,
    // Optional fields
    optionalField1: formValues.optionalField1,
    optionalField2: formValues.optionalField2,
    optionalField3: formValues.optionalField3,
    optionalField4: formValues.optionalField4,
    optionalField5: formValues.optionalField5,
    optionalField6: formValues.optionalField6
  };
}

/**
 * Transforms API response to form values for the consignment line details form.
 * The temperature range is read only for a cold chain line: the backend returns the bounds whatever
 * the flag, and lines without cold chain often keep stale ones (mostly 0 / 0).
 * @param lineDetails The API response data for a consignment line
 * @returns The form values populated with the API response data
 */
export function transformResponseToForm(lineDetails: ConsignmentLineDetailResponse): ConsignmentLineDetailsFormValues {
  const coldChain = lineDetails.isColdChain ?? false;
  const coldMinTemp = coldChain ? (lineDetails.coldMinTemp ?? null) : null;
  const coldMaxTemp = coldChain ? (lineDetails.coldMaxTemp ?? null) : null;

  return {
    productCategory: lineDetails.productCategory?.id || '',
    inventoryQuantity: lineDetails.inventoryQuantity,
    productLtuId: String(lineDetails.productLtuId),
    description: lineDetails.ownerItemDescription || '',
    handlingUnits: lineDetails.handlingUnits,
    handlingUnitTypeId: String(lineDetails.handlingUnitTypeId),
    totalWeight: lineDetails.totalWeightKg,
    totalVolume: lineDetails.totalVolumeM3,
    totalValue: lineDetails.totalValueUsd ?? undefined,
    ownerReference: lineDetails.ownerReference || '',
    expiryDate: lineDetails.expiryDate || '',
    batchNumber: lineDetails.batchNumber || '',
    productCode: lineDetails.productCode || '',
    subProjectCode: lineDetails.subproject?.subprojectCode || '',
    optionalField1: lineDetails.optionalField1,
    optionalField2: lineDetails.optionalField2,
    optionalField3: lineDetails.optionalField3,
    optionalField4: lineDetails.optionalField4,
    optionalField5: lineDetails.optionalField5,
    optionalField6: lineDetails.optionalField6,
    fragile: lineDetails.isFragile ?? false,
    dangerous: lineDetails.isDangerous ?? false,
    regulated: lineDetails.isRegulated ?? false,
    coldChain,
    coldChainProfile: matchColdChainProfile(coldMinTemp, coldMaxTemp) ?? '',
    coldMinTemp,
    coldMaxTemp,
    dangerousGoodsUnNumber: lineDetails.dangerousGoodsUnid?.toString() || '',
    handlingInstructions: lineDetails.handlingInstructions || '',
    showOnPackingList: lineDetails.showOnPackingList ?? false
  };
}
